import { canonicalList } from '../data/colombiaCities';
import { useState, useEffect } from 'react';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import toast from 'react-hot-toast';
import {
  setStoredFeatureOverride,
  getAllBusinessFeatures,
  buildEstiloWithFeatures,
  getCleanEstilo,
  getDirectMediaUrl,
  UNIVERSAL_MULTI_SELECTION_RULE
} from '../utils/businessConfigHelpers';

const WA_PROVISION_WEBHOOK = "https://flow.seryiads.com/webhook/02c73d5f-816f-475a-9696-6a40b53c97d8";

export const useConfig = () => {
  const { masterDb, activeBusinessId, setMasterDb } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isDemo = ['9001', '9002', '9003'].includes(String(activeBusinessId));
  const isSuperAdmin = userRole === 'master';
  const isReadOnlyDemo = isDemo && !isSuperAdmin;

  const activeBusiness = Array.isArray(masterDb) ? masterDb.find(b => String(b.id) === String(activeBusinessId)) : null;
  const isDemoAccount = isDemo && !isSuperAdmin;
  const isTrialAccount = !!activeBusiness && (activeBusiness.plan === 'free' || activeBusiness.subscription_status === 'trial') && !isSuperAdmin;

  const showNotification = (msg, type = 'success') => {
    if (type === 'error') toast.error(msg);
    else if (type === 'info') toast(msg, { icon: 'ℹ️' });
    else if (type === 'warning') toast(msg, { icon: '⚠️' });
    else toast.success(msg);
  };

  // Se aplica sobre el estado más reciente del almacén (no sobre la copia de este render): así dos cambios seguidos
  // (por ejemplo estilo y estilo_clean al escribir la identidad base) se suman en vez de pisarse y el segundo borrar al primero.
  const updateActiveBusinessField = (field, value) => {
    setMasterDb((current) => (Array.isArray(current) ? current : []).map(b => {
      if (String(b.id) === String(activeBusinessId)) {
        return { ...b, [field]: value };
      }
      return b;
    }));
  };

  const saveBusinessSettings = async (customFields = {}) => {
    if (!activeBusiness) return;

    if (isReadOnlyDemo) {
      showNotification('Modo solo lectura: Las cuentas demo no se pueden modificar.', 'error');
      return;
    }

    const cleanCustom = (customFields && typeof customFields === 'object' && !customFields.nativeEvent && !customFields.target && !customFields._reactName) ? customFields : {};

    const rawMenuUrl = cleanCustom.menu_pdf_url !== undefined ? cleanCustom.menu_pdf_url : activeBusiness.menu_pdf_url;
    const menuUrl = getDirectMediaUrl(rawMenuUrl);
    
    const rawLinkUrl = cleanCustom.menu_link_url !== undefined ? cleanCustom.menu_link_url : activeBusiness.menu_link_url;
    const rawTextList = cleanCustom.menu_text_list !== undefined ? cleanCustom.menu_text_list : activeBusiness.menu_text_list;

    if (menuUrl !== undefined) {
      setStoredFeatureOverride(activeBusinessId, 'menu_pdf_url', menuUrl);
    }
    if (rawLinkUrl !== undefined) {
      setStoredFeatureOverride(activeBusinessId, 'menu_link_url', rawLinkUrl);
    }
    if (rawTextList !== undefined) {
      setStoredFeatureOverride(activeBusinessId, 'menu_text_list', rawTextList);
    }

    // Los números en atención humana se toman de lo que se está guardando ahora; si no vienen, de lo que hay en pantalla.
    // Nunca de un valor viejo guardado en el navegador: así un número recién agregado (y su nombre) no se pierde al guardar.
    const currentMuted = Array.isArray(cleanCustom.muted_phones) ? cleanCustom.muted_phones : (Array.isArray(activeBusiness.muted_phones) ? activeBusiness.muted_phones : []);
    const currentMutedMetadata = (cleanCustom.muted_phones_metadata && typeof cleanCustom.muted_phones_metadata === 'object')
      ? cleanCustom.muted_phones_metadata
      : (activeBusiness.muted_phones_metadata || {});
    setStoredFeatureOverride(activeBusinessId, 'muted_phones', currentMuted);
    setStoredFeatureOverride(activeBusinessId, 'muted_phones_metadata', currentMutedMetadata);

    const currentFeatures = getAllBusinessFeatures(activeBusiness, { 
      menu_pdf_url: menuUrl,
      menu_link_url: rawLinkUrl,
      menu_text_list: rawTextList,
      muted_phones: currentMuted,
      muted_phones_metadata: currentMutedMetadata
    });
    
    let cleanEstiloInput = (cleanCustom.estilo_clean !== undefined && cleanCustom.estilo_clean !== null)
      ? cleanCustom.estilo_clean
      : (activeBusiness.estilo_clean && activeBusiness.estilo_clean.trim()
          ? activeBusiness.estilo_clean
          : getCleanEstilo(activeBusiness.estilo));

    if (cleanEstiloInput && !cleanEstiloInput.includes('REGLA UNIVERSAL DE TOMA MULTI-SELECCIÓN Y MULTI-CANTIDAD')) {
      cleanEstiloInput = `${cleanEstiloInput}\n\n${UNIVERSAL_MULTI_SELECTION_RULE}`;
    }

    const fullEstilo = buildEstiloWithFeatures(cleanEstiloInput, currentFeatures);

    const memoryState = {
      nombre_negocio: activeBusiness.nombre_negocio,
      tipo_negocio: activeBusiness.tipo_negocio,
      flujo_operativo: activeBusiness.flujo_operativo || 'citas',
      id_whatsapp: activeBusiness.id_whatsapp,
      google_calendar_id: activeBusiness.google_calendar_id,
      horario: activeBusiness.horario,
      tono: activeBusiness.tono,
      estilo: fullEstilo,
      estilo_clean: cleanEstiloInput,
      menu_pdf_url: menuUrl,
      menu_link_url: rawLinkUrl,
      menu_text_list: rawTextList,
      banner_url: activeBusiness.banner_url,
      logo_url: activeBusiness.logo_url,
      address: activeBusiness.address,
      instagram: activeBusiness.instagram,
      facebook: activeBusiness.facebook,
      minisite_theme_color: activeBusiness.minisite_theme_color,
      minisite_show_staff: activeBusiness.minisite_show_staff,
      services: activeBusiness.services || [],
      staff: activeBusiness.staff || [],
      appointments: activeBusiness.appointments || [],
      orders: activeBusiness.orders || [],
      ...cleanCustom
    };

    setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => String(b.id) === String(activeBusinessId) ? { ...b, ...memoryState } : b));

    const dbPayload = {
      nombre_negocio: cleanCustom.nombre_negocio ?? activeBusiness.nombre_negocio,
      tipo_negocio: cleanCustom.tipo_negocio ?? activeBusiness.tipo_negocio,
      flujo_operativo: cleanCustom.flujo_operativo ?? activeBusiness.flujo_operativo ?? 'citas',
      id_whatsapp: cleanCustom.id_whatsapp ?? activeBusiness.id_whatsapp,
      google_calendar_id: cleanCustom.google_calendar_id ?? activeBusiness.google_calendar_id,
      horario: cleanCustom.horario ?? activeBusiness.horario,
      tono: cleanCustom.tono ?? activeBusiness.tono,
      slug: cleanCustom.slug ?? activeBusiness.slug,
      estilo: fullEstilo,
      banner_url: cleanCustom.banner_url ?? activeBusiness.banner_url,
      logo_url: cleanCustom.logo_url ?? activeBusiness.logo_url,
      address: cleanCustom.address ?? activeBusiness.address,
      instagram: cleanCustom.instagram ?? activeBusiness.instagram,
      facebook: cleanCustom.facebook ?? activeBusiness.facebook,
      minisite_theme_color: cleanCustom.minisite_theme_color ?? activeBusiness.minisite_theme_color,
      minisite_show_staff: cleanCustom.minisite_show_staff ?? activeBusiness.minisite_show_staff
    };
    // Ciudades de entrega: la columna la crea la migración 20261007d; solo se manda si el negocio ya la tiene o se editó, para no romper el guardado del resto.
    const deliveryCities = cleanCustom.delivery_cities ?? activeBusiness.delivery_cities;
    if (Array.isArray(deliveryCities)) dbPayload.delivery_cities = canonicalList(deliveryCities);
    const paymentInfo = cleanCustom.payment_info ?? activeBusiness.payment_info;
    if (typeof paymentInfo === 'string') dbPayload.payment_info = paymentInfo;
    // Recordatorios de pago por transferencia: la columna la crea la migración 20261008f; solo se manda si el negocio ya la tiene o se cambió.
    const paymentReminders = cleanCustom.payment_reminders ?? activeBusiness.payment_reminders;
    if (typeof paymentReminders === 'boolean') dbPayload.payment_reminders = paymentReminders;

    try {
      const { error } = await supabase.from('businesses').update(dbPayload).eq('id', activeBusinessId);
      if (error) {
        console.error("[Supabase Error - Guardar Config]", error.message);
        showNotification(`Error al guardar en la base de datos: ${error.message}`, "error");
      } else {
        showNotification(`¡${activeBusiness.nombre_negocio} actualizado en la base de datos!`);
      }
    } catch (err) {
      console.error("Error al guardar en Postgres:", err);
      showNotification(`Error al guardar: ${err.message || 'No se pudo conectar a la base de datos'}`, "error");
    }
  };

  const updateBusinessFeature = async (businessId, field, value) => {
    if (isReadOnlyDemo) {
      showNotification('Modo solo lectura: Las cuentas demo no se pueden modificar.', 'error');
      return;
    }

    const targetBiz = (Array.isArray(masterDb) ? masterDb : []).find(b => String(b.id) === String(businessId));
    if (!targetBiz) return;

    if (field === 'whatsapp_enabled' && value === false && targetBiz.instance_name) {
      try {
        showNotification("Desconectando bot de WhatsApp...", "info");
        fetch(WA_PROVISION_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'delete_instance',
            instance_name: targetBiz.instance_name
          })
        }).catch(err => console.warn("Error asíncrono al desconectar n8n:", err));
      } catch (e) {
        console.error(e);
      }
    }

    setStoredFeatureOverride(businessId, field, value);
    const currentFeatures = getAllBusinessFeatures(targetBiz, { [field]: value });
    const updatedEstilo = buildEstiloWithFeatures(targetBiz.estilo_clean || targetBiz.estilo, currentFeatures);

    const previousValue = targetBiz[field];
    const previousEstilo = targetBiz.estilo;
    const applyState = (val, estilo) => setMasterDb((Array.isArray(masterDb) ? masterDb : []).map(b => String(b.id) === String(businessId) ? { 
      ...b, 
      [field]: val, 
      estilo, 
      estilo_clean: getCleanEstilo(estilo) 
    } : b));

    applyState(value, updatedEstilo);

    // El bot (n8n) lee la base de datos, no este navegador: solo confirmamos si de verdad se guardó.
    let saveError = null;
    try {
      const { error } = await supabase.from('businesses').update({ estilo: updatedEstilo, [field]: value }).eq('id', businessId);
      if (error) {
        const { error: fallbackError } = await supabase.from('businesses').update({ estilo: updatedEstilo }).eq('id', businessId);
        saveError = fallbackError;
      }
    } catch (err) {
      saveError = err;
    }

    if (saveError) {
      console.error("Error al guardar característica en Postgres:", saveError);
      setStoredFeatureOverride(businessId, field, previousValue);
      applyState(previousValue, previousEstilo);
      showNotification('No se pudo guardar el cambio en el servidor. Intenta de nuevo.', 'error');
      return;
    }
    showNotification(`Módulo ${value ? 'Habilitado' : 'Deshabilitado'} con éxito.`, "success");
  };

  return {
    activeBusiness,
    activeBusinessId,
    saveBusinessSettings,
    updateBusinessFeature,
    updateActiveBusinessField,
    showNotification,
    isSuperAdmin,
    isTrialAccount,
    isDemoAccount
  };
};
